import { CSSProperties, FC, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Divider } from 'antd';
import BasicInfo from './components/BasicInfo';
import TaskList from './components/TaskList';
import StatusRecords from './components/StatusRecords';
import styles from './index.module.less';
import { isNotEmptyAny } from '@lhb/func';
import { useSelector } from 'react-redux';
import { useMethods } from '@lhb/hook';
import Transfer from '@/common/components/business/ExpandStore/FranchiseeTransferModal';
import Share from '@/common/components/business/ExpandStore/FranchiseeShareModal';
import ShareInfo from './components/ShareInfo';
import FollowRecords from './components/FollowRecords';
import FollowRecordModal from './components/FollowRecordModal';

/**
 * 拓店任务详情，不含顶部操作按钮
 * @Description
 * 目前在拓店任务抽屉、拓店异动审批详情、机会点详情的关联任务信息栏使用
 */
const FranchiseeDetail: FC<{
  mainHeight: any;
  style: CSSProperties;
  detail: any;
  isUpdateMode: boolean;
  setIsUpdateMode: (state: boolean) => void;
  setSubmitting: (state: boolean) => void;
  refresh: () => void;
  franchiseeAlias: string;
  expansionTaskAlias: string;
  /** 关闭详情抽屉 */
  closeDrawer?: () => void;
  ref?: any,
}> = forwardRef(({
  mainHeight, // 从V2Container透传过来的，必须挂到scroll上
  style = {},
  detail,
  isUpdateMode,
  setIsUpdateMode,
  setSubmitting,
  refresh,
  franchiseeAlias,
  expansionTaskAlias,
  closeDrawer,
}, ref) => {

  // 外部调用保存加盟商
  useImperativeHandle(ref, () => ({
    confirmHandle: () => {
      (basicRef.current as any).confirmHandle();
    },
    transfer: methods.transfer,
    share: methods.share,
    followRecord: methods.followRecord,
  }));

  const basicRef: any = useRef(null);
  const [recrodsRefreshFlag, setRecordsRefreshFlag] = useState<number>(0);

  const [taskTemplateCode, setTaskTemplateCode] = useState<string>('');
  const [taskTemplateEnable, setTaskTemplateEnable] = useState<boolean>(false);
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);

  const transferRef = useRef<any>(null);
  const shareRef = useRef<any>(null);
  const followRecordRef = useRef<any>(null);
  const [followRefreshFlag, setFollowRefreshFlag] = useState<number>(0); // 跟进记录刷新标识

  useEffect(() => {
    const { taskTemplateCode: templateCode, taskTemplateEnable: taskEnable } = tenantCheckData;
    templateCode && setTaskTemplateCode(templateCode);
    setTaskTemplateEnable(!!taskEnable);
  }, [tenantCheckData]);

  const methods = useMethods({

    /**
     * @description 基本信息完成编辑后的刷新回调
     */
    handleBasicRefresh: () => {
      // 触发刷新状态变更记录
      setRecordsRefreshFlag((state) => ++state);
      // 回调刷新
      refresh();
    },

    /** 移交 */
    transfer() {
      transferRef.current?.init();
    },
    /** 移交成功回调 */
    transferComplete() {
      // // 触发刷新状态变更记录
      // setRecordsRefreshFlag((state) => ++state);
      // 回调刷新
      refresh();
      closeDrawer?.();
    },

    /** 共享 */
    share() {
      shareRef.current?.init();
    },
    /** 共享成功回调 */
    shareComplete() {
      refresh();
    },

    /** 写跟进 */
    followRecord() {
      followRecordRef.current?.init();
    },
    /** 写跟进成功回调：仅刷新跟进记录模块 */
    followRecordComplete() {
      setFollowRefreshFlag((state) => ++state);
      refresh();
    },

  });

  return (
    isNotEmptyAny(detail) ? <div
      className={styles.franchiseeDetail}
      style={{
        height: mainHeight || 'auto',
        ...style
      }}>

      {/* 基本信息动态表单 */}
      <BasicInfo
        ref={basicRef}
        detail={detail}
        isUpdateMode={isUpdateMode}
        setIsUpdateMode={setIsUpdateMode}
        setSubmitting={setSubmitting}
        refresh={methods.handleBasicRefresh}
      />
      <Divider/>

      {/* 任务列表 */}
      {taskTemplateEnable && taskTemplateCode ? <>
        <TaskList franchiseeId={detail.id} refresh={refresh} taskTemplateCode={taskTemplateCode} expansionTaskAlias={expansionTaskAlias}/>
        <Divider/>
      </> : <></>}

      {/* 状态变更 */}
      <StatusRecords franchiseeId={detail.id} refreshFlag={recrodsRefreshFlag} franchiseeAlias={franchiseeAlias}/>

      {/* 共享信息 */}
      {isNotEmptyAny(detail.franchiseeAffiliation2) ? <>
        <Divider/>
        <ShareInfo
          franchiseeId={detail.id}
          franchiseeAffiliation2={detail.franchiseeAffiliation2}
          franchiseeAffiliation2Name={detail.franchiseeAffiliation2Name}
          franchiseeAffiliation2Time={detail.franchiseeAffiliation2Time}
          onSuccess={methods.shareComplete}
        />
      </> : <></>}

      {/* 跟进记录 */}
      <Divider/>
      <FollowRecords franchiseeId={detail.id} refreshFlag={followRefreshFlag} />

      {/* 移交 */}
      <Transfer ref={transferRef} franchiseeId={detail.id} onSuccess={methods.transferComplete} />

      {/* 写跟进 */}
      <FollowRecordModal
        ref={followRecordRef}
        franchiseeId={detail.id}
        onSuccess={methods.followRecordComplete}
      />

      {/* 共享 */}
      <Share
        ref={shareRef}
        curBelonger={detail.affiliation}
        franchiseeId={detail.id}
        franchiseeName={detail.name}
        franchiseeAlias={franchiseeAlias}
        franchiseeAffiliation2Name={detail.franchiseeAffiliation2Name}
        onSuccess={methods.shareComplete}
      />

    </div> : <></>
  );
});

export default FranchiseeDetail;
